import React from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { router } from "./router";
import { Toaster } from "./components/ui/sonner";
import { ApiError } from "./lib/api";
import "./index.css";

const queryClient = new QueryClient({
  // Without this, a failed `queryFn` is fully silent — its error sits on
  // `query.error` and most callers don't read it. Log queryKey + status +
  // message only; never the response body (ApiError.body could carry server
  // detail we don't want in the console under HIPAA).
  queryCache: new QueryCache({
    onError: (error, query) => {
      const status = error instanceof ApiError ? error.status : undefined;
      console.warn("[react-query] queryFn failed:", {
        queryKey: query.queryKey,
        status,
        message: error.message,
      });
    },
  }),
  defaultOptions: {
    queries: {
      // 30s keeps quick back-and-forth navigation from re-hitting the API
      // while still picking up changes the user made elsewhere.
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: true,
    },
  },
});

async function start() {
  // Dev-only UI sandbox: VITE_MOCK_CONSULT=1 patches fetch with an in-memory
  // consult + attachments so the TEL-101 panels render without auth/API/GCS.
  // Dead code in prod builds — both env flags are compile-time constants.
  if (import.meta.env.DEV && import.meta.env.VITE_MOCK_CONSULT) {
    await import("./dev/mock-consult-api");
  }

  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <Toaster position="bottom-right" />
      </QueryClientProvider>
    </React.StrictMode>,
  );
}

void start();
